import React, { ReactNode } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  SensorDescriptor,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaSort } from 'react-icons/fa';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import { MdDragIndicator } from 'react-icons/md';
import { ColumnTooltipIcon } from '@/features/ai-agents/chat/MarkdownTableVisualization/ColumnTooltipIcon';
import {
  DragHandle,
  HeaderContent,
  HeaderLabel,
  SortButton,
  SortIndicator,
  Table,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/styles';
import {
  ParsedTable,
  RenderedTable,
  SortedTableRow,
  SortDirection,
  TableSort,
  TooltipTextResolver,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import { reorderColumns } from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

interface TableViewProps {
  currentSort: TableSort | null;
  getTooltip?: TooltipTextResolver;
  onColumnOrderChange: (columnOrder: number[]) => void;
  onSort: (columnIndex: number) => void;
  orderedColumnIndexes: number[];
  renderedTable: RenderedTable;
  sensors: SensorDescriptor<any>[];
  sortedRows: SortedTableRow[];
  table: ParsedTable;
}

interface SortableTableHeaderProps {
  children: ReactNode;
  columnIndex: number;
  direction?: SortDirection;
  label: string;
  onSort: () => void;
  tooltipText: string | null;
}

export function TableView({
  currentSort,
  getTooltip,
  onColumnOrderChange,
  onSort,
  orderedColumnIndexes,
  renderedTable,
  sensors,
  sortedRows,
  table,
}: TableViewProps) {
  const handleColumnDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    onColumnOrderChange(
      reorderColumns(orderedColumnIndexes, Number(active.id), Number(over.id)),
    );
  };

  return (
    <TableWrapper>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleColumnDragEnd}
      >
        <Table>
          <Thead>
            <Tr>
              <SortableContext
                items={orderedColumnIndexes}
                strategy={horizontalListSortingStrategy}
              >
                {orderedColumnIndexes.map((columnIndex) => (
                  <SortableTableHeader
                    key={columnIndex}
                    columnIndex={columnIndex}
                    label={table.headers[columnIndex]}
                    direction={
                      currentSort?.columnIndex === columnIndex
                        ? currentSort.direction
                        : undefined
                    }
                    onSort={() => onSort(columnIndex)}
                    tooltipText={
                      getTooltip?.(table.headers[columnIndex]) ?? null
                    }
                  >
                    {renderedTable.headers[columnIndex] ??
                      table.headers[columnIndex]}
                  </SortableTableHeader>
                ))}
              </SortableContext>
            </Tr>
          </Thead>
          <Tbody>
            {sortedRows.map((row) => (
              <Tr key={row.originalIndex}>
                {orderedColumnIndexes.map((columnIndex) => (
                  <Td key={columnIndex}>
                    {renderedTable.rows[row.originalIndex]?.[columnIndex] ??
                      row.values[columnIndex]}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </DndContext>
    </TableWrapper>
  );
}

function SortableTableHeader({
  children,
  columnIndex,
  direction,
  label,
  onSort,
  tooltipText,
}: SortableTableHeaderProps) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: columnIndex });

  return (
    <Th
      ref={setNodeRef}
      style={{
        opacity: isDragging ? 0.6 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      aria-sort={direction ?? `none`}
    >
      <HeaderContent>
        <DragHandle
          type="button"
          aria-label={`Move ${label} column`}
          title={`Drag to move ${label} column`}
          {...attributes}
          {...listeners}
        >
          <MdDragIndicator aria-hidden />
        </DragHandle>
        <SortButton
          role="button"
          tabIndex={0}
          onClick={onSort}
          onKeyDown={(event) => {
            if (event.key === `Enter` || event.key === ` `) {
              event.preventDefault();
              onSort();
            }
          }}
        >
          <HeaderLabel>{children}</HeaderLabel>
          <ColumnTooltipIcon label={label} text={tooltipText} />
          <SortIndicator aria-hidden>
            {direction === `ascending` && <FiArrowUp />}
            {direction === `descending` && <FiArrowDown />}
            {!direction && <FaSort />}
          </SortIndicator>
        </SortButton>
      </HeaderContent>
    </Th>
  );
}

export function MarkdownTableHead({ children }: { children: ReactNode }) {
  return <Thead>{children}</Thead>;
}

export function MarkdownTableBody({ children }: { children: ReactNode }) {
  return <Tbody>{children}</Tbody>;
}

export function MarkdownTableRow({ children }: { children: ReactNode }) {
  return <Tr>{children}</Tr>;
}

export function MarkdownTableHeader({ children }: { children: ReactNode }) {
  return <Th>{children}</Th>;
}

export function MarkdownTableCell({ children }: { children: ReactNode }) {
  return <Td>{children}</Td>;
}
